import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useCurrentUser, isSuperAdminRole } from '../lib/auth'

export const Route = createFileRoute('/settings_/hidden')({
  component: HiddenSettingsPage,
})

function HiddenSettingsPage() {
  const { data: me, isLoading: meLoading } = useCurrentUser()
  const qc = useQueryClient()
  const isSuper = isSuperAdminRole(me)

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'settings', 'hidden'],
    queryFn: () => api.admin.settings.getHidden(),
    enabled: isSuper,
  })

  const mut = useMutation({
    mutationFn: (patch: Record<string, string>) => api.admin.settings.updateHidden(patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'settings', 'hidden'] })
      qc.invalidateQueries({ queryKey: ['auth-methods'] })
    },
  })

  if (meLoading) return <Shell><p className="text-[var(--muted)]">Hleð…</p></Shell>

  if (!isSuper) {
    return (
      <Shell>
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-6 text-amber-900">
          <h2 className="font-semibold">Enginn aðgangur</h2>
          <p className="mt-1 text-sm">Aðeins superadmin kemst hér inn.</p>
        </div>
      </Shell>
    )
  }

  const settings = data?.settings ?? []

  return (
    <Shell>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[var(--fg)]">Faldar stillingar</h1>
        <p className="text-sm text-[var(--muted)] mt-1">
          Rofar fyrir fítusa sem eru tilbúnir en ekki komnir í loftið. Þessi síða er hvergi tengd í
          valmynd og birtist ekki á venjulega stillingaskjánum. Meðan slökkt er svara endapunktar
          fítussins 404 og ekkert í viðmótinu gefur til kynna að hann sé til.
        </p>
      </div>

      {isLoading && <p className="text-[var(--muted)]">Sæki stillingar…</p>}
      {error && <p className="text-red-500">{(error as Error).message}</p>}
      {mut.error && <p className="text-red-500">{(mut.error as Error).message}</p>}

      {data && settings.length === 0 && (
        <p className="text-[var(--muted)]">Engir faldir fítusar sem stendur.</p>
      )}

      <div className="space-y-3">
        {settings.map((s) => {
          const on = s.value === 'true'
          return (
            <div
              key={s.key}
              className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-5 shadow-[var(--elev-sm)]"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="font-semibold text-[var(--fg)]">{s.label}</h2>
                  <p className="text-xs font-mono text-[var(--faint)] mt-0.5">{s.key}</p>
                  {s.description && (
                    <p className="text-sm text-[var(--muted)] mt-2 max-w-2xl">{s.description}</p>
                  )}
                </div>
                <button
                  type="button"
                  disabled={mut.isPending}
                  onClick={() => mut.mutate({ [s.key]: on ? 'false' : 'true' })}
                  className={`rounded-lg px-3.5 py-2 text-sm font-semibold transition-colors disabled:opacity-50 ${
                    on
                      ? 'border border-[var(--border)] text-[var(--muted)] hover:border-red-300 hover:text-red-600'
                      : 'bg-[var(--brand)] text-[var(--on-brand)] hover:bg-[var(--brand-strong)]'
                  }`}
                >
                  {on ? 'Slökkva' : 'Kveikja'}
                </button>
              </div>
              <p className="mt-3 text-sm">
                <span
                  className={`rounded-full border px-2.5 py-1 text-[11px] font-medium ${
                    on
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                      : 'border-slate-200 bg-slate-100 text-slate-500'
                  }`}
                >
                  {on ? 'Kveikt — sýnilegt notendum' : 'Slökkt — falið'}
                </span>
              </p>
            </div>
          )
        })}
      </div>
    </Shell>
  )
}

function Shell({ children }: { children: React.ReactNode }) {
  return <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8 py-8">{children}</div>
}
